import * as React from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'defaultChecked' | 'onChange'> {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

/**
 * Plan View's checkbox (`.ds-checkbox`): a 16px box on the strong line that
 * fills with `primary` and shows a check when chosen, the accent only then.
 *
 * A native input underneath, so the browser keeps its keyboard and
 * screen-reader behavior; only its drawing is replaced. It takes the chip
 * corner, not the control one: a 16px box with an 8px corner is a circle, and a
 * circle is what a radio looks like. Put it in a `<label>` with its words
 * (`inline-flex items-center gap-2`), as the recipe does.
 */
export function Checkbox({ checked, onCheckedChange, className, ...props }: CheckboxProps) {
  return (
    <span className={cn('relative inline-flex h-4 w-4 shrink-0', className)}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onCheckedChange(event.target.checked)}
        className="peer m-0 h-4 w-4 appearance-none rounded-sm border border-line-strong bg-surface transition-colors duration-100 ease-out checked:border-primary checked:bg-primary focus-visible:focus-ds disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
        {...props}
      />
      <Check
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 m-auto hidden h-3 w-3 stroke-[3] text-on-primary peer-checked:block"
      />
    </span>
  )
}
